
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getAdminSession, hydrateAdminSession } from '@/lib/admin-auth';
import type { PainelPermission, PainelRole } from '@/lib/painel-rbac';
import type { AdminSession } from '@/services/auth.service';

type PainelSessionContextValue = {
  session: AdminSession | null;
  role: PainelRole | null;
  permissions: PainelPermission[];
  hydrated: boolean;
  refresh: () => void;
};

const PainelSessionContext = createContext<PainelSessionContextValue | null>(null);

export function PainelSessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AdminSession | null>(() => getAdminSession());
  const [hydrated, setHydrated] = useState(false);

  const refresh = useCallback(() => {
    const cached = getAdminSession();
    if (cached) {
      setSession(cached);
    }

    void (async () => {
      const remote = await hydrateAdminSession();
      setSession(remote ?? getAdminSession());
      setHydrated(true);
    })();
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      session,
      role: session?.role ?? null,
      permissions: session?.permissions ?? [],
      hydrated,
      refresh,
    }),
    [session, hydrated, refresh]
  );

  return <PainelSessionContext.Provider value={value}>{children}</PainelSessionContext.Provider>;
}

export function usePainelSession(): PainelSessionContextValue {
  const ctx = useContext(PainelSessionContext);
  if (!ctx) {
    throw new Error('usePainelSession must be used within PainelSessionProvider');
  }
  return ctx;
}
